import type { CategoryId, InstrumentTypeId, JurisdictionId, ObligationLevel, StatusId, TimelineKind } from './types'

export const NAVIGATOR_BASE_PATH = '/uae-sustainability-regulations'
export const LIBRARY_VERSION = '1.0.0'
export const LIBRARY_REVIEWED = '2026-10-13'

export const STANDARD_DISCLAIMER =
  'Preliminary planning information prepared by Ecopshub. It is not legal advice, a compliance determination, regulatory assurance, or a substitute for the official Arabic text of UAE legislation. Where an English translation is cited, the Arabic text prevails if there is a conflict. A law taking effect does not, by itself, create a universal filing deadline.'

export const JURISDICTION_LABELS: Record<JurisdictionId, string> = {
  federal: 'Federal UAE',
  abu_dhabi: 'Abu Dhabi',
  dubai: 'Dubai',
  sharjah: 'Sharjah',
  ajman: 'Ajman',
  uaq: 'Umm Al Quwain',
  rak: 'Ras Al Khaimah',
  fujairah: 'Fujairah',
  multiple_emirates: 'Multiple emirates',
  free_zones: 'Relevant free zones',
  international: 'International rules affecting UAE businesses',
}

export const CATEGORY_LABELS: Record<CategoryId, string> = {
  climate: 'Climate change',
  ghg: 'GHG emissions',
  carbon_registration: 'Carbon registration and credits',
  esg_disclosure: 'ESG disclosure',
  sustainable_finance: 'Sustainable finance',
  climate_financial_risk: 'Climate-related financial risk',
  waste: 'Waste and circular economy',
  environment: 'Environmental protection and pollution',
  energy: 'Energy and renewables',
  water: 'Water',
  biodiversity: 'Biodiversity',
  labour: 'Labour and worker welfare',
  product_supply_chain: 'Product and supply-chain sustainability',
  cross_border: 'Cross-border sustainability requirements',
  other: 'Other relevant sustainability topics',
}

export const INSTRUMENT_LABELS: Record<InstrumentTypeId, string> = {
  federal_law: 'Federal law',
  federal_decree_law: 'Federal decree-law',
  cabinet_resolution: 'Cabinet resolution',
  ministerial_decision: 'Ministerial decision',
  executive_regulation: 'Executive regulation',
  emirate_law: 'Emirate-level law',
  local_decision: 'Local authority decision',
  financial_regulation: 'Financial-sector regulation',
  exchange_guidance: 'Exchange guidance',
  policy: 'Policy or strategy',
  voluntary_framework: 'Voluntary framework',
  consultation: 'Consultation or proposed instrument',
  market_expectation: 'Market or contractual expectation',
  international_instrument: 'International instrument',
}

export const STATUS_LABELS: Record<StatusId, string> = {
  in_force: 'In force',
  forthcoming: 'Forthcoming',
  under_consultation: 'Under consultation',
  amended: 'Amended — still operative',
  repealed: 'Repealed',
  historical: 'Historical',
  needs_verification: 'Verification required',
}

export const OBLIGATION_LABELS: Record<ObligationLevel, string> = {
  mandatory: 'Mandatory where the stated conditions are met',
  conditional: 'Conditional — depends on designation, threshold or activity',
  voluntary: 'Voluntary',
  informational: 'Informational — not a company filing duty by itself',
}

export const TIMELINE_KIND_LABELS: Record<TimelineKind, string> = {
  historical: 'Historical event',
  in_force: 'Currently in force',
  confirmed_future: 'Confirmed future deadline',
  proposed: 'Proposed development',
  consultation: 'Consultation',
  expected: 'Expected milestone',
  amended: 'Amended instrument',
  repealed: 'Repealed instrument',
}

export const EMIRATE_OPTIONS = [
  { id: 'abu_dhabi', label: 'Abu Dhabi' },
  { id: 'dubai', label: 'Dubai' },
  { id: 'sharjah', label: 'Sharjah' },
  { id: 'ajman', label: 'Ajman' },
  { id: 'uaq', label: 'Umm Al Quwain' },
  { id: 'rak', label: 'Ras Al Khaimah' },
  { id: 'fujairah', label: 'Fujairah' },
  { id: 'not_sure', label: 'Not sure' },
  { id: 'none', label: 'No UAE establishment' },
] as const

export const SECTOR_OPTIONS = [
  { id: 'energy', label: 'Energy and utilities' },
  { id: 'oil_gas', label: 'Oil, gas and petrochemicals' },
  { id: 'manufacturing', label: 'Manufacturing and industry' },
  { id: 'construction', label: 'Construction and contracting' },
  { id: 'real_estate', label: 'Real estate and buildings' },
  { id: 'logistics', label: 'Logistics and transport' },
  { id: 'healthcare', label: 'Healthcare' },
  { id: 'banking', label: 'Banking' },
  { id: 'insurance', label: 'Insurance' },
  { id: 'other_financial', label: 'Other financial services' },
  { id: 'professional', label: 'Professional and business services' },
  { id: 'hospitality', label: 'Hospitality and retail' },
  { id: 'agriculture', label: 'Agriculture and food' },
  { id: 'waste', label: 'Waste and environmental services' },
  { id: 'aviation', label: 'Aviation' },
  { id: 'other', label: 'Other' },
  { id: 'not_sure', label: 'Not sure' },
] as const

export const ACTIVITY_OPTIONS = [
  { id: 'outdoor_work', label: 'Outdoor or open-air work' },
  { id: 'industrial_operations', label: 'Industrial or process operations' },
  { id: 'waste_handling', label: 'Waste generation, handling or treatment' },
  { id: 'high_energy', label: 'High energy or fuel use' },
  { id: 'goods_manufacture', label: 'Manufacture of physical goods' },
  { id: 'not_sure', label: 'Not sure' },
] as const

export const HIGH_EMISSION_SECTORS = ['energy', 'oil_gas', 'manufacturing', 'waste', 'aviation', 'construction']

export function labelOf<T extends string>(map: Record<T, string>, id: string): string {
  return (map as Record<string, string>)[id] || id
}
